import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { channelService, ConnectWhatsAppCredentials } from '../services/channel.service';
import { queryKeys } from './query-keys';
import type { Channel } from '../entities/channel.entity';

export function useChannels() {
  return useQuery({
    queryKey: queryKeys.channels.all(),
    queryFn: () => channelService.loadAll(),
  });
}

export function useCreateChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (channel: Omit<Channel, 'id' | 'createdAt' | 'updatedAt'>) =>
      channelService.create(channel),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.channels.all() });
    },
  });
}

export function useUpdateChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, channel }: { id: string; channel: Partial<Channel> }) =>
      channelService.updateById(id, channel),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.channels.all() });
    },
  });
}

export function useSoftDeleteChannel() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => channelService.softDelete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.channels.all() });
    },
  });
}

export function useConnectWhatsApp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (credentials: ConnectWhatsAppCredentials) =>
      channelService.connectWhatsApp(credentials),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.channels.all() });
    },
  });
}

export function useDisconnectWhatsApp() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (channelId: string) => channelService.disconnectWhatsApp(channelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.channels.all() });
    },
  });
}
